// -----------------------------------------------------------------------------
// ADMIN CONSOLE helpers (Part 3, v0.3.0) — pure functions, unit-tested in tests/unit/admin.test.ts.
// The database checks every rule again (supabase/migrations/0008_admin.sql); these only shape the screens.
// -----------------------------------------------------------------------------
import type { Access, AdminPerson, AdminRole, IdName } from "./api/types";

/** One "project + role" row in Add member / Edit member. */
export interface Assignment { code: string; role_id: string }

export const PROJECT_CODE_RE = /^BIM-\d{3}$/;
export const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** A7.4 list filter: search name / email, one project, one role ("" = all). */
export function memberMatches(p: AdminPerson, q: string, code: string, roleId: string): boolean {
  const s = q.trim().toLowerCase();
  if (s && !`${p.name} ${p.email}`.toLowerCase().includes(s)) return false;
  if (code && !p.projects.some((x) => x.code === code)) return false;
  if (roleId) {
    if (roleId === "super_admin") return p.is_super_admin;
    if (!p.projects.some((x) => x.role_id === roleId && (!code || x.code === code))) return false;
  }
  return true;
}

export const STATUS_PILL: Record<AdminPerson["status"], string> = { Active: "p-closed", Invited: "p-invited", Deactivated: "p-void-plain" };

/** Problems with a list of project + role rows (Add member / Edit member) — empty list = fine. */
export function assignmentProblems(rows: Assignment[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const r of rows) {
    if (!r.code) { out.push("Pick a project on every row"); continue; }
    if (!r.role_id) out.push(`Pick a role for ${r.code}`);
    if (seen.has(r.code)) out.push(`${r.code} is listed twice`);
    seen.add(r.code);
  }
  return [...new Set(out)];
}

/** Problems in the Add member form. Projects are optional (round 17 #5) except in the limited view (needsProject). */
export function addMemberProblems(name: string, email: string, rows: Assignment[], needsProject = false): string[] {
  const out: string[] = [];
  if (!name.trim()) out.push("Type the person's full name");
  if (!EMAIL_RE.test(email.trim())) out.push("Enter a valid email address");
  if (needsProject && rows.length === 0) out.push("Add at least one project");
  return [...out, ...assignmentProblems(rows)];
}

/** Problems in the Add / Edit project panel. */
export function projectProblems(code: string, name: string): string[] {
  const out: string[] = [];
  if (!name.trim()) out.push("Type the project name");
  if (!PROJECT_CODE_RE.test(code.trim().toUpperCase())) out.push("Project code must look like BIM-031 (BIM- and 3 digits)");
  return out;
}

/** A7.3 Add role: "Copies Issues Tracker: Edit · Sign-off: off · Project setup: off". */
export function copySummary(role: AdminRole | undefined, modules: IdName[], built: (id: string) => boolean): string {
  if (!role) return "";
  const label: Record<Access, string> = { edit: "Edit", view: "View", none: "No access" };
  const mods = modules.filter((m) => built(m.id)).map((m) => `${m.name}: ${label[role.access[m.id] ?? "none"]}`);
  return `Copies ${[...mods, `Sign-off: ${role.can_signoff ? "on" : "off"}`, `Project setup: ${role.can_setup ? "on" : "off"}`].join(" · ")}`;
}

/** A7.3 Save changes: only the roles that changed (nothing changed → []). Includes Unassigned projects (0009). */
export function changedRoles(before: AdminRole[], after: AdminRole[]): Pick<AdminRole, "id" | "access" | "can_signoff" | "can_setup" | "unassigned_access">[] {
  return after
    .filter((r) => {
      const b = before.find((x) => x.id === r.id);
      return !b || b.can_signoff !== r.can_signoff || b.can_setup !== r.can_setup || b.unassigned_access !== r.unassigned_access
        || JSON.stringify(b.access) !== JSON.stringify(r.access);
    })
    .map(({ id, access, can_signoff, can_setup, unassigned_access }) => ({ id, access, can_signoff, can_setup, unassigned_access }));
}

/** Company roles in the dropdown (0010: Administrator first — only the super admin may pick it). */
export const COMPANY_ROLES: { value: string; label: string }[] = [
  { value: "admin", label: "Administrator" }, { value: "gm", label: "GM" }, { value: "project_head", label: "Project head" }, { value: "", label: "Project member" }];
/** Company roles that are on every project automatically (no project rows). */
export const EVERY_PROJECT = (c: string | null | undefined) => c === "admin" || c === "gm";

/** Round 17 #6: what the "Change company role?" pop-up says (every change asks). */
export function roleChangeText(from: string | null, to: string, hasProjects: boolean): string {
  if (to === "admin") return "Administrators run BuildNexx with you and see every project; their project roles are removed when you save. Only you appoint Administrators; an Administrator can take the role away from another one, never from themselves.";
  if (from === "admin") return `Their Administrator rights end when you save${to === "gm" ? "; as GM they stay on every project" : "; add projects in Manage projects afterwards"}.`;
  if (to === "gm") return "A GM is on every project automatically; their project roles are removed when you save.";
  if (to === "project_head") return hasProjects ? "A Project head leads every project they are on; their project roles become Project head when you save." : "A Project head leads the projects you add for them in Manage projects.";
  return from === "project_head" && hasProjects ? "Their Project head projects are cleared; add projects with a role in Manage projects after saving." : "They become a Project member: add projects with a role in Manage projects.";
}
export const companyLabel = (c: string | null | undefined, superAdmin = false): string =>
  superAdmin ? "Super admin" : COMPANY_ROLES.find((x) => x.value === (c ?? ""))?.label ?? "Project member";

/** A7.1 Manage members / A7.2.2 Manage projects: one row per person (or project) and what Save would do. */
export interface PickRow { key: string; on: boolean; role: string; was: { on: boolean; role: string } }
/** Rows that changed → {set: [{key, role}], remove: [key]} (unticked/removed rows go to remove only if they were on). */
export function pickChanges(rows: PickRow[]): { set: { key: string; role: string }[]; remove: string[] } {
  const set: { key: string; role: string }[] = [], remove: string[] = [];
  for (const r of rows) {
    if (r.on && (!r.was.on || r.role !== r.was.role)) set.push({ key: r.key, role: r.role });
    if (!r.on && r.was.on) remove.push(r.key);
  }
  return { set, remove };
}
/** Ticked rows that still need a role (project members only). */
export const missingRoles = (rows: PickRow[], needsRole: (key: string) => boolean): string[] =>
  rows.filter((r) => r.on && !r.was.on && needsRole(r.key) && !r.role).map((r) => r.key);
